import { SearchResultDisplayedProps } from "src/hooks/useSegment";
import { type CatalogBrand, type CatalogProduct } from "src/types/graphql.d";
import { Locale } from "src/lib/Intl";
import type {
  ProductSuggestion,
  Suggestion,
  SuggestionType,
} from "src/types/adapters/query.type";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { ShopType } from "src/utils/genderLocale/genderLocale";

const MAX_DISPLAYED_RESULTS_TOTAL = 450;

export function formatResultsTotal(total: number): string {
  return total > MAX_DISPLAYED_RESULTS_TOTAL
    ? `${MAX_DISPLAYED_RESULTS_TOTAL}+`
    : String(total);
}

export function productSuggestionToCatalogProduct(
  product: ProductSuggestion
): CatalogProduct {
  const brand: CatalogBrand = {
    id: product.brandId,
    name: product.brandName,
    designer_brand: false,
    gate: { level: product.gateLevel },
    good_on_you_pillars: [],
    is_good_on_you: false,
  };

  return {
    id: product.id,
    name: product.name,
    price: product.price,
    regular_price: product.regularPrice,
    slug: product.slug,
    thumbnail: product.thumbnail,
    currency: product.currency,
    labels: product.labels,
    gate: { level: product.gateLevel },
    product_shop_type: product.genders,
    available_sizes: product.sizes,
    brand,
  };
}

export function getSearchResultDisplayedProps({
  searchTerm,
  products,
  suggestions,
  resultId,
}: {
  searchTerm: string;
  products: ProductSuggestion[];
  suggestions: Suggestion[];
  resultId?: string;
}): SearchResultDisplayedProps {
  const collections = suggestions
    .filter((suggestion) => suggestion.type === "collection")
    .map((suggestion) => suggestion.id);
  const brands = suggestions
    .filter((suggestion) => suggestion.type === "brand")
    .map((suggestion) => suggestion.id);
  const categories = suggestions
    .filter((suggestion) => suggestion.type === "category")
    .map((suggestion) => suggestion.id);

  return {
    query: searchTerm,
    result_id: resultId,
    num_brands_returned: brands.length,
    num_categories_returned: categories.length,
    num_collections_returned: collections.length,
    num_products_returned: products.length,
    search_result_brands: brands,
    search_result_categories: categories,
    search_result_collections: collections,
    search_result_products: products.map((product, idx) => ({
      brand: product.brandName,
      brand_id: product.brandId,
      category: [],
      category_id: [],
      labels: product.labels?.map((label) => label.value) || [],
      name: product.name,
      position: idx,
      price: product.price,
      product_discount:
        Math.round(
          (product.regularPrice - product.price + Number.EPSILON) * 100
        ) / 100,
      product_id: product.id,
      product_kids_gender: [],
      product_shop_type: product.genders,
      regular_price: product.regularPrice,
      sizes_available: product.sizes.slice().sort(),
      url: `/product/${product.slug}`,
      gate_level: product.gateLevel,
    })),
  };
}

type SuggestionRouteConfig = {
  noShopType: string;
  withShopType: string;
};

function getSuggestionRoutingConfig(type: SuggestionType, locale: Locale) {
  const routesConfig: Partial<Record<SuggestionType, SuggestionRouteConfig>> = {
    brand: {
      noShopType: `${getI18nRoute({ route: "/brands", locale })}/:slug`,
      withShopType: `${getI18nRoute({
        route: "/brands",
        locale,
      })}/:slug:gender`,
    },
    category: {
      noShopType: `${getI18nRoute({ route: "/products", locale })}/:slug`,
      withShopType: ":gender/:slug",
    },
    collection: {
      noShopType: `${getI18nRoute({ route: "/collections", locale })}/:slug`,
      withShopType: `${getI18nRoute({
        route: "/collections",
        locale,
      })}:gender/:slug`,
    },
  };

  return routesConfig[type];
}

export function getSuggestionItemLink({
  suggestion,
  shopType,
  locale,
}: {
  suggestion: Suggestion;
  shopType: string;
  locale: Locale;
}): string {
  const config = getSuggestionRoutingConfig(
    suggestion.type as SuggestionType,
    locale
  );

  if (!config || !suggestion.slug) {
    return suggestion.url ?? "";
  }

  if (shopType === ShopType.All) {
    return config.noShopType.replace(":slug", suggestion.slug);
  }

  const gender = getI18nRoute({ route: `/${shopType}`, locale });

  return config.withShopType
    .replace(":slug", suggestion.slug)
    .replace(":gender", gender);
}
